﻿import {nanoid} from "@2d-front-monorepo/core";
import React, {JSX, memo, useCallback, useMemo, useState} from "react";
import {TabPanelProps} from "./TabPanelProps";
import {TabsProps} from "./TabsProps";

type TabButtonProps = {
	id: string;
	panelId: string
	title: string;
	selected: boolean;
	onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

function TabButton({title, id, panelId, selected, onClick}: TabButtonProps): JSX.Element {
	return <li role="presentation">
		<button id={id}
		        aria-controls={panelId}
		        type="button"
		        role="tab"
		        aria-selected={selected ? true : undefined}
		        tabIndex={selected ? 0 : -1}
		        onClick={onClick}>
			{title}
		</button>
	</li>;
}

function Tabs(props: TabsProps): JSX.Element {
	const tabPanels = React.Children.toArray(props.children) as React.ReactElement<TabPanelProps>[];
	const tabIds = useMemo(() => tabPanels.map(() => "tab_" + nanoid()), [tabPanels.length]);
	const panelIds = useMemo(() => tabPanels.map((_, i) => "panel_" + tabIds[i]), [tabIds]);
	const [selected, setSelected] = useState(props.selected ?? 0);
	const klass = useMemo(() => "tabs" + (props.className ? " " + props.className : ""), [props.className]);

	const onClick = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
		setSelected(tabIds.indexOf(e.currentTarget.id));
	}, []);

	return <div className={klass}>
		<ul role="tablist">
			{tabPanels.map((tabPanel, index) => (
				<TabButton title={tabPanel.props.title} id={tabIds[index]} panelId={panelIds[index]} key={index}
				           selected={index === selected} onClick={onClick}/>
			))}
		</ul>

		<div role="tabpanel" aria-labelledby={tabIds[selected]} id={panelIds[selected]}>
			{tabPanels[selected]}
		</div>
	</div>;
}

const MemoizedTabs: React.FC<TabsProps> = memo(Tabs);
export default MemoizedTabs;
